//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { Fragment, useContext, useState } from 'react';
import { Avatar, IconButton, makeStyles, MenuItem, Menu, Divider } from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { STRIPE_API_PUBLISHABLE_KEY } from '../../system/constants';
import { LayoutContext, AppContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import {
    CONFIRM_DIALOG_MESSAGE_RESEND_MEMBER_INVITE,
    CONFIRM_DIALOG_TITLE_RESEND_MEMBER_INVITE,
    EMAIL_REGEX,
    INVITATION_DECLINED,
    INVITATION_SENT,
    CONFIRM_DIALOG_TITLE_CONFIRM_DELETE_MEMBER,
    CONFIRM_DIALOG_MESSAGE_CONFIRM_DELETE_MEMBER,
    CANCEL_ALL,
    ADD_REMOVE,
    BUY_NOW,
    UPLOADED_VIDEO_STATUS_PROCESSING,
} from '../../common/constants';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import {
    removeInvitee,
    removeMember,
    resendInvitation,
    changeInviteeEmail,
    toggleMembersRole,
    changeMemberEmail,
    downgradeAdditionalSeatSubscription,
    resetEmpMemberPictureSeResults,
} from '../../common/services/empMySettingsServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdEditDialog from '../../common/elemental/AfitdEditDialog';
import AfitdTextField from '../../common/elemental/AfitdTextField';
import AfitdConfirmDialog from '../../common/elemental/AfitdConfirmDialog';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import InviteSection from './InviteSection';
import EditProfileDialog from './EditProfileDialog';
import { VARIANT_OWNER, VARIANT_EXISTING_MEMBER } from './EditProfileDialog';
import ChangePasswordDialog from './ChangePasswordDialog';
import PaymentDialog from './PaymentDialog';
import ReplaceOwnerDialog from './ReplaceOwnerDialog';
import OrderDialog from './OrderDialog';
import CancelAdditionalSeatDialog from './CancelAdditionalSeatDialog';
import AfitdMessageDialog from '../../common/elemental/AfitdMessageDialog';

import { extractBadContentFromResult } from '../../common/sightEngineHelper';
import LoadingGif from '../../assets/loading.gif';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';
import _map from 'lodash/map';
import _find from 'lodash/find';
import _noop from 'lodash/noop';
import _some from 'lodash/some';
import _filter from 'lodash/filter';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => ({
        heading: { fontWeight: 'bold' },
        sectionTitle: { fontWeight: 'bold', marginTop: 20 },

        listContainer: { display: 'flex', alignItems: 'center', padding: '15px 0', borderBottom: '2px solid rgb(240,242,246)' },
        displayPicture: { color: 'rgb(252,130,31)', background: 'rgba(252, 130, 31, 0.1)', marginRight: 20, height: 50, width: 50 },
        name: { fontWeight: 'bold' },
        email: { fontWeight: 'bold' },
        grayedText: { color: 'rgb(155,158,156)' },
        invitationStatusText: { color: 'rgb(252,130,31)' },

        inline: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '10px 20px 0 0' },
        userInfo: { flexGrow: 1 },
        removeOption: { color: 'red' },
        hidden: { display: 'none' },

        seatsToRemove: { background: 'rgba(29,138,139,0.09)', padding: 10, marginTop: 20, fontWeight: 'bold', borderRadius: 4 },

        vacantSeatsText: { color: 'rgb(29,138,139)', fontWeight: 'bold' },
        additionalSeatsAddedText: { color: 'rgb(29,138,139)', fontWeight: 'bold' },
        additionalSeatsContainer: { background: '#FFF8E5', padding: 10, marginTop: 20, borderRadius: 4, display: 'flex', justifyContent: 'space-between', cursor: 'pointer' },
        additionalSeatsText: { fontWeight: 'bold' },
        cancelAdditionalSeatsButton: { color: 'rgb(252,130,31,1)', fontWeight: 'bold' },
        additionalSeatsButton: { color: 'rgb(252,130,31,1)', fontWeight: 'bold' },
        divider: {
            color: 'grey',
            paddingLeft: '10px',
            paddingRight: '10px',
        },
        removeOwnerOption: { color: 'red' },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        props,
        currentEmployer,
        showMenuForInvitee,
        setShowMenuForInvitee,
        inviteeEmailModal,
        setInviteeEmailModal,
        newEmail,
        setNewEmail,
        handleChangeEmailAddress,
        newEmailError,
        editProfileModalOpen,
        setEditProfileModalOpen,
        selectedUser,
        members,
        // membersNew,
        changePasswordModal,
        setChangePasswordModal,
        tiers,
        subscribedTier,
        currentTier,
        additionalSeats,
        handleResendInvitation,
        resendInvitationOpen,
        setResendInvitationOpen,
        // paymentModalFlag,
        // setPaymentModalFlag,
        showMenuForAdmin,
        setShowMenuForAdmin,
        deleteMemberOpen,
        setDeleteMemberOpen,
        handleDeleteTeamMember,
        replaceOwnerDialogOpen,
        setReplaceOwnerDialogOpen,
        orderMode,
        setOrderMode,
        purchaseMode,
        setPurchaseMode,
        cancelAdditionalSeatModalOpen,
        setCancelAdditionalSeatModalOpen,
        handleClickCancelAdditionalSeatsButton,
        handleOrderDialogProceedToPayment,
        handleOrderDialogSubmitDowngradeOrder,
        invoices,
        // setInvoices,
        handlePaymentDialogSuccessPurchase,
        badImageDialogMessage,
        openBadImageDialog,
        setOpenBadImageDialog,
        employerId,
        setSnackbarMessage,
        setSnackbarErrorMessage,
    } = params;

    const { hidden: hiddenCls, seatsToRemove: seatsToRemoveCls, additionalSeatsContainer: additionalSeatsContainerCls } = classes;

    const { isAdmin } = props;

    const stripeAdditionalSeatSubId = currentEmployer?.stripeAdditionalSeatSubId;
    const invited = _get(currentEmployer, 'invited', []);
    const vacantSeats = tiers?.find(el => el.tier === subscribedTier)?.seatMax + (subscribedTier === 3 ? additionalSeats : 0) - members.length - invited.length;
    const showVacantSeats = subscribedTier !== 1 && vacantSeats >= 0;

    const selectedUserIsOwner = !!selectedUser?.isOwner;

    const additionalSeatsButtonLabel = stripeAdditionalSeatSubId ? ADD_REMOVE : BUY_NOW;
    const cancelAdditionalSeatsButtonIsHidden = !stripeAdditionalSeatSubId;
    const orderDialogOpen = !!orderMode;
    const paymentDialogOpen = !!purchaseMode;

    const defns = {
        heading: { variant: 'h6', value: 'Users' },
        sectionTitle: { variant: 'body2' },

        name: { variant: 'body1' },
        grayedText: { variant: 'body1', component: 'span' },
        email: { variant: 'body2' },

        captionText: { variant: 'caption' },
        invitationStatusText: { variant: 'body2', value: INVITATION_SENT },

        inviteSection: { isAdmin, vacantSeats, isHidden: subscribedTier < 2 },

        menuForInvitee: { anchorEl: showMenuForInvitee, keepMounted: true, open: Boolean(showMenuForInvitee), onClose: () => setShowMenuForInvitee(null) },
        moreButtonForInvitee: { 'aria-haspopup': true, onClick: evt => setShowMenuForInvitee(evt.currentTarget) },

        menuForAdmin: { anchorEl: showMenuForAdmin, keepMounted: true, open: Boolean(showMenuForAdmin), onClose: () => setShowMenuForAdmin(null) },
        moreButtonForAdmin: { 'aria-haspopup': true, onClick: evt => setShowMenuForAdmin(evt.currentTarget) },

        changeInviteeEmail: {
            open: inviteeEmailModal,
            onClose: () => setInviteeEmailModal(false),
            label: 'Change Email Address',
            actionButtonDefns: {
                save: { disabled: !newEmail, onClick: handleChangeEmailAddress, label: 'Change' },
            },
        },
        changeEmailField: { onChange: evt => setNewEmail(evt.target.value), value: newEmail, placeholder: 'New email address', error: newEmailError },
        newEmailErrorText: { variant: 'body2', className: !newEmailError && hiddenCls, value: 'Please enter a valid email address' },

        editProfileModal: {
            variant: selectedUserIsOwner ? VARIANT_OWNER : VARIANT_EXISTING_MEMBER,
            openEditModal: editProfileModalOpen,
            memberUserId: selectedUser?.userId,
            profilePicUrl: selectedUser?.profilePicUrl,
            firstName: selectedUser?.firstName,
            middleName: selectedUser?.middleName,
            lastName: selectedUser?.lastName,
            country: selectedUser?.country,
            state: selectedUser?.state,
            city: selectedUser?.city,
            cityLocation: selectedUser?.cityLocation,
            isPictureinProcess: selectedUser?.uploadedPictureStatus === UPLOADED_VIDEO_STATUS_PROCESSING,
            // closeEditModal: () => setEditProfileModalOpen(false),
            onCancel: () => setEditProfileModalOpen(false),
            onClose: () => setEditProfileModalOpen(false),
            onDidSave: () => setEditProfileModalOpen(false),
            onProfilePicDidSave: () => setEditProfileModalOpen(false),
            onProfilePicDidDelete: () => setEditProfileModalOpen(false),
            // selectedUser,
            // members,
            // membersNew,
        },

        changePasswordModal: { openChangePasswordModal: changePasswordModal, closeChangePasswordModal: () => setChangePasswordModal(false) },

        confirmResendInvitationModal: {
            open: resendInvitationOpen,
            onClose: () => setResendInvitationOpen(false),
            onConfirm: handleResendInvitation,
            title: CONFIRM_DIALOG_TITLE_RESEND_MEMBER_INVITE,
            message: CONFIRM_DIALOG_MESSAGE_RESEND_MEMBER_INVITE,
        },

        confirmDeleteMemberDialog: {
            open: deleteMemberOpen,
            onClose: () => setDeleteMemberOpen(false),
            onConfirm: handleDeleteTeamMember,
            title: CONFIRM_DIALOG_TITLE_CONFIRM_DELETE_MEMBER,
            message: CONFIRM_DIALOG_MESSAGE_CONFIRM_DELETE_MEMBER,
            noButtonLabel: 'Cancel',
        },

        seatsToRemove: { variant: 'body2', className: showVacantSeats ? seatsToRemoveCls : hiddenCls },
        vacantSeatsText: { variant: 'body2', value: `${vacantSeats} vacant`, component: 'span' },
        additionalSeatsAddedText: { variant: 'body2', value: `${additionalSeats} additional`, component: 'span' },

        additionalSeatsContainer: {
            variant: 'body2',
            className: isAdmin && subscribedTier ? additionalSeatsContainerCls : hiddenCls,
            onClick: () => {
                console.log('>>>>>>>>>> click');
                setOrderMode('additional-seat');
            },
        },
        additionalSeatsText: { variant: 'body2', value: 'Need additional seats?' },
        cancelAdditionalSeatsButton: { variant: 'body1', value: CANCEL_ALL, isHidden: cancelAdditionalSeatsButtonIsHidden, onClick: handleClickCancelAdditionalSeatsButton },
        additionalSeatsButton: { variant: 'body1', value: additionalSeatsButtonLabel },

        cancelAdditionalSeatModal: {
            additionalSeats,
            open: cancelAdditionalSeatModalOpen,
            onClose: () => setCancelAdditionalSeatModalOpen(false),
        },

        orderDialog: {
            open: orderDialogOpen,
            variant: orderMode,
            onClose: () => setOrderMode(null),
            selectedTier: currentTier, //orderMode === 'additional-seat' || orderMode === 'interval' ? currentTier : tiers.find(el => el.tier === selectedTier),
            setIsDirtyFlagNull: _noop, //() => setSelectedTier(0),
            shouldUpdate: true, //Boolean(subscribedTier),
            onProceedToPayment: handleOrderDialogProceedToPayment,
            onSubmitDowngradeOrder: handleOrderDialogSubmitDowngradeOrder,
            onCancelAdditionSeatsButtonClick: handleClickCancelAdditionalSeatsButton,
        },
        paymentModal: {
            open: paymentDialogOpen,
            variant: purchaseMode,
            onClose: () => setPurchaseMode(null),
            selectedTier: currentTier, //purchaseMode === 'additional-seat' || purchaseMode === 'interval' ? currentTier : tiers.find(el => el.tier === selectedTier),
            setIsDirtyFlagNull: _noop, //() => setSelectedTier(0),
            shouldUpdate: true, //Boolean(subscribedTier),
            onSuccessPurchase: handlePaymentDialogSuccessPurchase,
            // isSubscribedTier3: isSubscribedToTier3,
            // monthly,
            invoices,
        },
        // paymentModal: {
        //     openPaymentDialog: paymentModalFlag,
        //     closePaymentModal: () => setPaymentModalFlag(false),
        //     selectedTier: tiers.find(el => el.tier === (tier === 3 ? tier : tier + 1)),
        //     isSuscribedTier3: tier === 3,
        //     // setIsDirtyFlagNull: () => setSelectedTier(0),
        //     shouldUpdate: true,
        // },
        replaceOwnerDialog: {
            onClose: () => setReplaceOwnerDialogOpen(false),
            open: replaceOwnerDialogOpen,
            // conversations,
            // limitExceeded: engagementLimit,
            // jobSeekerId,
            // jobSeekerName,
            // jobSeekerProfilePicUrl,
        },
        badImageDialog: {
            title: 'Inappropriate Images are not Allowed!',
            children: (
                <AfitdTypography>
                    We detected <b>{badImageDialogMessage}</b> in your picture which does not meet our community guidelines. Inappropriate Images cannot be saved to this platform.
                </AfitdTypography>
            ),
            open: openBadImageDialog,
            onClose: async () => {
                setOpenBadImageDialog();
                setEditProfileModalOpen(false);
                await resetEmpMemberPictureSeResults({ employerId, setSnackbarMessage, setSnackbarErrorMessage });
            },
        },
    };
    // inject classes
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(STRIPE_API_PUBLISHABLE_KEY);

const MemberMoreMenu = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { user, isAdmin, onMoreButtonClick, onMenuOptionClick } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [menuAnchorEl, setMenuAnchorEl] = useState(false);

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleClickMoreIconButton = evt => {
        setMenuAnchorEl(evt.currentTarget);
        onMoreButtonClick && onMoreButtonClick();
    };

    const handleClickMenuOption = mode => {
        setMenuAnchorEl(null);
        onMenuOptionClick && onMenuOptionClick(mode, user);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = {
        moreButtonForMember: { 'aria-haspopup': true, onClick: handleClickMoreIconButton },
        menuForMember: { anchorEl: menuAnchorEl, keepMounted: true, open: Boolean(menuAnchorEl), onClose: () => setMenuAnchorEl(null) },
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div>
            <IconButton {...defns.moreButtonForMember}>
                <ArrowDropDownIcon />
            </IconButton>
            <Menu {...defns.menuForMember}>
                {/* {!isAdmin && (
                    <>
                        <MenuItem onClick={() => handleClickMenuOption('changePassword')}>Change Password</MenuItem>
                        <MenuItem onClick={() => handleClickMenuOption('changeEmail')}>Change Email</MenuItem>
                    </>
                )} */}
                {isAdmin && (
                    <MenuItem disabled={user?.isAdmin} onClick={() => handleClickMenuOption('admin')}>
                        Admin
                    </MenuItem>
                )}
                {isAdmin && (
                    <>
                        <MenuItem disabled={!user?.isAdmin} onClick={() => handleClickMenuOption('user')}>
                            User
                        </MenuItem>
                        <Divider />
                    </>
                )}
                <MenuItem onClick={() => handleClickMenuOption('edit')}>Update Information</MenuItem>
                {isAdmin && (
                    <>
                        <Divider />
                        <MenuItem onClick={() => handleClickMenuOption('remove')} {...defns.removeOption}>
                            Remove
                        </MenuItem>
                    </>
                )}
            </Menu>
        </div>
    );
};

const InviteeMoreMenu = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { invitee, isInvitationDeclined, onMenuOptionClick } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [menuAnchorEl, setMenuAnchorEl] = useState(false);

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleClickMoreIconButton = evt => {
        setMenuAnchorEl(evt.currentTarget);
        // onMoreButtonClick && onMoreButtonClick();
    };

    const handleClickMenuOption = mode => {
        setMenuAnchorEl(null);
        onMenuOptionClick && onMenuOptionClick(mode, invitee);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = {
        moreButtonForInvitee: { 'aria-haspopup': true, onClick: handleClickMoreIconButton },
        menuForInvitee: { anchorEl: menuAnchorEl, keepMounted: true, open: Boolean(menuAnchorEl), onClose: () => setMenuAnchorEl(null) },
        resendInviteMenuItem: { onClick: () => handleClickMenuOption('resendInvite') },
        removeOption: { onClick: () => handleClickMenuOption('remove') },
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div>
            <div>
                User Invited
                <IconButton {...defns.moreButtonForInvitee}>
                    <ArrowDropDownIcon />
                </IconButton>
            </div>
            <Menu {...defns.menuForInvitee}>
                {/* <MenuItem onClick={() => handleSelectMenuOption('changeEmail', el)}>Change email address</MenuItem> */}
                {/* <Divider /> */}
                {!isInvitationDeclined && <MenuItem {...defns.resendInviteMenuItem}>Resend Invite</MenuItem>}
                {!isInvitationDeclined && <Divider />}
                <MenuItem {...defns.removeOption}>Remove</MenuItem>
            </Menu>
        </div>
    );
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const Users = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();

    const appContext = useContext(AppContext);
    const { currentEmployer, employerId, userId, sys, setThankYouForPurchaseDialogOpen } = appContext;
    const trialAvailable = _get(currentEmployer, 'trialAvailable');

    const members = _get(currentEmployer, 'members', []);
    const membersNew = _get(currentEmployer, 'membersNew', []);
    const invited = _get(currentEmployer, 'invited', []);
    const inviteDeclined = _get(currentEmployer, 'inviteDeclined', []);
    const subscribedTier = _get(currentEmployer, 'tier', 0);
    const additionalSeats = _get(currentEmployer, 'additionalSeats', 0);
    const tiers = _get(sys, 'tiers', []);

    const layoutContext = useContext(LayoutContext);
    const { setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { isAdmin } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [showMenuForInvitee, setShowMenuForInvitee] = useState(null);
    const [showMenuForAdmin, setShowMenuForAdmin] = useState(null);
    const [inviteeEmailModal, setInviteeEmailModal] = useState(false);
    const [changePasswordModal, setChangePasswordModal] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [newEmailError, setNewEmailError] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    const [editProfileModalOpen, setEditProfileModalOpen] = useState(false);
    const [resendInvitationOpen, setResendInvitationOpen] = useState(false);
    const [deleteMemberOpen, setDeleteMemberOpen] = useState(false);

    const [paymentModalFlag, setPaymentModalFlag] = useState(false);
    const [replaceOwnerDialogOpen, setReplaceOwnerDialogOpen] = useState(false);

    const [orderMode, setOrderMode] = useState(null);
    const [purchaseMode, setPurchaseMode] = useState(null);
    const [cancelAdditionalSeatModalOpen, setCancelAdditionalSeatModalOpen] = useState(false);
    const [invoices, setInvoices] = useState();
    const [badImageDialogMessage, setBadImageDialogMessage] = useState([]);
    const [openBadImageDialog, setOpenBadImageDialog] = useState(false);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // Detect SightEngine Response
    React.useEffect(() => {
        if (members) {
            // eslint-disable-next-line no-unused-expressions
            members?.forEach(member => {
                const seResult = _get(member, 'seResult');
                if (seResult) extractBadContentFromResult({ seResult, setBadImageDialogMessage, setOpenBadImageDialog });
            });
        }
    }, [members, editProfileModalOpen]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const owner = members.find(el => el.isOwner);
    const isOwnerPictureInProcessing = owner?.uploadedPictureStatus === UPLOADED_VIDEO_STATUS_PROCESSING;
    const otherMembers = members.filter(el => !el.isOwner);

    const isOwnerLoggedIn = userId === owner?.userId;
    const currentTier = tiers.find(t => t.tier === subscribedTier);

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleResendInvitation = () => {
        resendInvitation({
            email: selectedUser,
            employerId,
            // callback: ({ code } = {}) => {
            //     //temporarily display invitation link for testers
            //     // alert(`https://${AFITD_SHARE_URL}/ei/${code}`);
            // },
            setSnackbarMessage,
            setSnackbarErrorMessage,
        });
        setResendInvitationOpen(false);
    };

    // Delete Team Member
    const handleDeleteTeamMember = () => {
        const user = selectedUser;
        if (user?.userId) {
            removeMember(
                [..._filter(members, el => el.userId !== user.userId)],
                _some(membersNew, el => el.userId === user.userId) ? _filter(membersNew, el => el.userId !== user.userId) : membersNew,
                employerId,
                setSnackbarMessage,
                setSnackbarErrorMessage,
            );
        } else removeInvitee(user, [...invited.filter(el => el !== user)], employerId, setSnackbarMessage, setSnackbarErrorMessage);

        setDeleteMemberOpen(false);
    };

    const handleChangeEmailAddress = () => {
        if (newEmail.match(EMAIL_REGEX)) {
            setNewEmailError(false);
            selectedUser?.userId
                ? changeMemberEmail(
                      newEmail,
                      [...members.filter(el => el.userId !== selectedUser.userId), { ...selectedUser, email: newEmail }],
                      membersNew.find(el => el.userId === selectedUser.userId)
                          ? [...membersNew.filter(el => el.userId !== selectedUser.userId), { ...selectedUser, email: newEmail }]
                          : membersNew,
                      employerId,
                      setSnackbarMessage,
                      setSnackbarErrorMessage,
                  )
                : changeInviteeEmail(newEmail, selectedUser, [...invited.filter(el => el !== selectedUser), newEmail], employerId, setSnackbarMessage, setSnackbarErrorMessage);
            setNewEmail('');
            setInviteeEmailModal(false);
        } else {
            setNewEmailError(true);
        }
    };

    const handleSelectMenuOption = (mode, user) => {
        setShowMenuForInvitee(null);
        setShowMenuForAdmin(null);
        setSelectedUser(user);

        switch (mode) {
            case 'changePassword':
                return setChangePasswordModal(true);
            case 'changeEmail':
                return setInviteeEmailModal(true);
            case 'resendInvite':
                return setResendInvitationOpen(true);
            case 'remove':
                return setDeleteMemberOpen(true);
            case 'user':
                return toggleMembersRole(
                    [...members.filter(el => el.userId !== user.userId), { ...user, isAdmin: false }],
                    membersNew.find(el => el.userId === user.userId) ? [...membersNew.filter(el => el.userId !== user.userId), { ...user, isAdmin: false }] : membersNew,
                    employerId,
                    setSnackbarMessage,
                    setSnackbarErrorMessage,
                );
            case 'admin':
                return toggleMembersRole(
                    [...members.filter(el => el.userId !== user.userId), { ...user, isAdmin: true }],
                    membersNew.find(el => el.userId === user.userId) ? [...membersNew.filter(el => el.userId !== user.userId), { ...user, isAdmin: true }] : membersNew,
                    employerId,
                    setSnackbarMessage,
                    setSnackbarErrorMessage,
                );
            case 'edit':
                // return user.isOwner ? setEditProfileModalOpen(true) : setEditMemberProfileModalOpen(true);
                return setEditProfileModalOpen(true);
            default:
                return;
        }
    };

    const handleReplaceOwner = () => {
        setShowMenuForInvitee(null);
        setShowMenuForAdmin(null);
        setReplaceOwnerDialogOpen(true);
    };

    const handleClickCancelAdditionalSeatsButton = evt => {
        evt.stopPropagation();
        setCancelAdditionalSeatModalOpen(true);
    };

    const handleOrderDialogProceedToPayment = ({ tier, monthly, isAdditionalSeatOrder, invoices } = {}) => {
        setInvoices(invoices);
        // setMonthly(monthly); // n/a
        // setIsAdditionalSeatOrder(isAdditionalSeatOrder); // always true in here
        // setPaymentModalFlag(true);
        setPurchaseMode(orderMode);
        setOrderMode(null);
    };

    const handleOrderDialogSubmitDowngradeOrder = async ({
        // isAdditionalSeatOrder,
        // tierNew,
        // billMonthlyNew,
        additionalSeatsNew,
        employerId,
        // stripeSubId,
        stripeAdditionalSeatSubId,
        // existingTier,
        // existingMonthly,
        existingAdditionalSeats,
        // setIsSubmittingDowngrade,
    } = {}) => {
        // submit additional seat downgrade order (removing seats)
        downgradeAdditionalSeatSubscription({
            additionalSeatsNew,
            employerId,
            stripeAdditionalSeatSubId,
            existingAdditionalSeats,
            setSnackbarMessage,
            setSnackbarErrorMessage,
            callback: () => {
                // close order dialog if successful
                setOrderMode(null);
            },
        });
    };

    const handlePaymentDialogSuccessPurchase = () => {
        setPurchaseMode(false);
        setThankYouForPurchaseDialogOpen(true);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        props,
        currentEmployer,
        showMenuForInvitee,
        setShowMenuForInvitee,
        inviteeEmailModal,
        setInviteeEmailModal,
        newEmail,
        setNewEmail,
        handleChangeEmailAddress,
        newEmailError,
        editProfileModalOpen,
        setEditProfileModalOpen,
        selectedUser,
        members,
        membersNew,
        changePasswordModal,
        setChangePasswordModal,
        tiers,
        subscribedTier,
        currentTier,
        additionalSeats,
        handleResendInvitation,
        resendInvitationOpen,
        setResendInvitationOpen,
        paymentModalFlag,
        setPaymentModalFlag,
        showMenuForAdmin,
        setShowMenuForAdmin,
        deleteMemberOpen,
        setDeleteMemberOpen,
        handleDeleteTeamMember,
        replaceOwnerDialogOpen,
        setReplaceOwnerDialogOpen,
        orderMode,
        setOrderMode,
        purchaseMode,
        setPurchaseMode,
        cancelAdditionalSeatModalOpen,
        setCancelAdditionalSeatModalOpen,
        handleClickCancelAdditionalSeatsButton,
        handleOrderDialogProceedToPayment,
        handleOrderDialogSubmitDowngradeOrder,
        invoices,
        setInvoices,
        handlePaymentDialogSuccessPurchase,
        badImageDialogMessage,
        setBadImageDialogMessage,
        openBadImageDialog,
        setOpenBadImageDialog,
        employerId,
        setSnackbarMessage,
        setSnackbarErrorMessage,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <Elements stripe={stripePromise}>
            <AfitdTypography {...defns.heading} />
            <InviteSection {...defns.inviteSection} />
            <AfitdTypography {...defns.sectionTitle}>Owner / Admin</AfitdTypography>
            <div {...defns.listContainer}>
                <Avatar {...defns.displayPicture} src={!isOwnerPictureInProcessing ? owner?.profilePicUrl : LoadingGif}>
                    ?
                </Avatar>
                <div {...defns.userInfo}>
                    <AfitdTypography {...defns.name}>
                        {owner?.firstName} {owner?.middleName} {owner?.lastName}
                        {isOwnerLoggedIn && <AfitdTypography {...defns.grayedText}> (You)</AfitdTypography>}
                    </AfitdTypography>
                    <AfitdTypography {...defns.email}>{owner?.email}</AfitdTypography>
                </div>
                {isOwnerLoggedIn && (
                    <div>
                        <IconButton {...defns.moreButtonForAdmin}>
                            <ArrowDropDownIcon />
                        </IconButton>
                        <Menu {...defns.menuForAdmin}>
                            <MenuItem onClick={() => handleSelectMenuOption('edit', owner)}>Update Information</MenuItem>
                            <MenuItem {...defns.removeOwnerOption} onClick={() => handleReplaceOwner()}>
                                Replace Owner / Admin
                            </MenuItem>
                        </Menu>
                    </div>
                )}
            </div>
            {subscribedTier >= 2 && (
                <Fragment>
                    <AfitdTypography {...defns.sectionTitle}>Team Members</AfitdTypography>
                    <div {...defns.inline}>
                        <AfitdTypography {...defns.captionText}>Name</AfitdTypography>
                        <AfitdTypography {...defns.captionText}>Role(s)</AfitdTypography>
                    </div>
                </Fragment>
            )}
            {subscribedTier >= 2 &&
                invited?.map(el => {
                    const isInvitationDeclined = !!_find(inviteDeclined, email => email === el);
                    return (
                        <div {...defns.listContainer}>
                            <Avatar {...defns.displayPicture}>?</Avatar>
                            <div {...defns.userInfo}>
                                <AfitdTypography {...defns.grayedText}>{el}</AfitdTypography>
                                <AfitdTypography {...defns.invitationStatusText} value={isInvitationDeclined ? INVITATION_DECLINED : INVITATION_SENT} />
                            </div>
                            {isAdmin && <InviteeMoreMenu invitee={el} isInvitationDeclined={isInvitationDeclined} onMenuOptionClick={handleSelectMenuOption} />}
                        </div>
                    );
                })}
            {subscribedTier >= 2 &&
                _map(otherMembers, (el, idx) => (
                    <div {...defns.listContainer}>
                        <Avatar {...defns.displayPicture} src={el?.uploadedPictureStatus === UPLOADED_VIDEO_STATUS_PROCESSING ? LoadingGif : el?.profilePicUrl}>
                            ?
                        </Avatar>
                        <div {...defns.userInfo}>
                            <AfitdTypography {...defns.name}>
                                {el?.firstName} {el?.middleName} {el?.lastName}
                                {el.userId === userId && <AfitdTypography {...defns.grayedText}> (You)</AfitdTypography>}
                            </AfitdTypography>
                            <AfitdTypography {...defns.email}>{el?.email}</AfitdTypography>
                        </div>
                        {(isAdmin || el.userId === userId) && (
                            <>
                                <span>{el?.isAdmin ? 'Admin' : 'User'}</span>&nbsp;
                                <MemberMoreMenu isAdmin={isAdmin} user={el} onMenuOptionClick={handleSelectMenuOption} />
                            </>
                        )}
                    </div>
                ))}
            {subscribedTier >= 2 && (
                <AfitdTypography {...defns.seatsToRemove}>
                    You have <AfitdTypography {...defns.vacantSeatsText} /> seats for this plan
                    {additionalSeats ? (
                        <>
                            <span {...defns.divider}>|</span> You added <AfitdTypography {...defns.additionalSeatsAddedText} /> seats to your plan
                        </>
                    ) : null}
                </AfitdTypography>
            )}

            {!trialAvailable && subscribedTier > 2 && (
                <div {...defns.additionalSeatsContainer}>
                    <AfitdTypography {...defns.additionalSeatsText} />
                    <AfitdTypography {...defns.cancelAdditionalSeatsButton} />
                    <AfitdTypography {...defns.additionalSeatsButton} />
                </div>
            )}

            <AfitdEditDialog {...defns.changeInviteeEmail}>
                <AfitdTextField {...defns.changeEmailField} />
                <AfitdTypography {...defns.newEmailErrorText} />
            </AfitdEditDialog>
            {editProfileModalOpen && <EditProfileDialog {...defns.editProfileModal} />}
            <ChangePasswordDialog {...defns.changePasswordModal} />
            <AfitdConfirmDialog {...defns.confirmResendInvitationModal} />
            <AfitdConfirmDialog {...defns.confirmDeleteMemberDialog} />
            <OrderDialog {...defns.orderDialog} />
            <PaymentDialog {...defns.paymentModal} />
            <ReplaceOwnerDialog {...defns.replaceOwnerDialog} />
            <CancelAdditionalSeatDialog {...defns.cancelAdditionalSeatModal} />
            <AfitdMessageDialog {...defns.badImageDialog} />
        </Elements>
    );
};

export default Users;
